// Firestore API for WhoIsPaying game
// Replaces legacy Google Apps Script backend

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  getDocs,
  increment,
} from 'firebase/firestore';
import { auth } from '../firebaseApp.js';
import { getDb } from '../firestoreRegistry.js';
import { signInAnonymously } from 'firebase/auth';

const db = getDb('whoispaying');
const defaultDb = getDb('default');

async function recordWhoIsPayingUsage({ roomId, gameKey, award, playerCount }) {
  await ensureAuth();
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return;

  const statsRef = doc(defaultDb, 'userGameStats', user.uid);
  const now = serverTimestamp();
  const safeCount = Number.isFinite(playerCount) && playerCount > 0 ? playerCount : 0;

  const statsUpdate = {
    userId: user.uid,
    updatedAt: now,
    'profile.displayName': user.displayName || null,
    'profile.email': user.email || null,
    'games.whoispaying.lastPlayedAt': now,
    'games.whoispaying.lastRoomId': roomId,
    'games.whoispaying.lastGameKey': gameKey || null,
    'games.whoispaying.lastAward': award || null,
    'games.whoispaying.totalGamesPlayed': increment(1),
  };

  if (safeCount > 0) {
    statsUpdate['games.whoispaying.lastPlayerCount'] = safeCount;
    statsUpdate['games.whoispaying.totalPlayersTracked'] = increment(safeCount);
  }

  console.log('[recordWhoIsPayingUsage] Writing stats for user:', user.uid, {
    roomId,
    gameKey,
    award,
    playerCount: safeCount,
  });

  await setDoc(statsRef, statsUpdate, { merge: true });

  console.log('[recordWhoIsPayingUsage] Stats write completed for user:', user.uid);
}

/**
 * Generate a 6-digit room code
 */
function generateRoomId() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Generate a unique player ID
 */
function generatePlayerId(name) {
  const prefix = name.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 10) || 'PLAYER';
  const suffix = Math.floor(100 + Math.random() * 900);
  return `${prefix}${suffix}`;
}

/**
 * Global singleton promise to ensure we only sign in once
 */
let authPromise = null;

/**
 * Wait for Firebase Auth to be ready - SINGLETON pattern to avoid race conditions
 */
async function ensureAuth() {
  // Fast path - already signed in
  if (auth.currentUser) {
    console.log('[ensureAuth] Fast path: user already authed', auth.currentUser.uid);
    return auth.currentUser;
  }

  // If we're already in the process of signing in, reuse that promise
  if (authPromise) {
    console.log('[ensureAuth] Reusing existing auth promise');
    return authPromise;
  }

  console.log('[ensureAuth] No current user, waiting for auth state...');
  
  // Wait for auth state to initialize (in case there's a persisted user loading)
  const waitForAuthState = new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
  
  const persistedUser = await waitForAuthState;
  if (persistedUser) {
    console.log('[ensureAuth] Persisted user found:', persistedUser.uid);
    return persistedUser;
  }
  
  console.log('[ensureAuth] No persisted user, starting anonymous sign-in...');
  
  // Try anonymous sign-in directly and race with a timeout so we surface useful errors quickly
  const signIn = (async () => {
    try {
      const cred = await signInAnonymously(auth);
      const user = cred?.user ?? auth.currentUser;
      console.log('[ensureAuth] Sign-in successful:', user?.uid);
      return user;
    } catch (err) {
      console.error('[ensureAuth] Sign-in error:', err);
      // Provide clearer guidance for common local/prod misconfigs
      const code = err?.code || err?.message || '';
      if (code.includes('auth/configuration-not-found')) {
        throw new Error('Anonymous Auth provider is disabled. Enable Anonymous sign-in in Firebase Console (Authentication > Sign-in method) or run with local emulators.');
      }
      if (code.includes('auth/operation-not-supported-in-this-environment')) {
        throw new Error('Auth operation not supported in this environment. If running tests/headless, use emulators or run in a supported browser.');
      }
      // Network and emulator not running cases
      if (code.includes('Failed to fetch') || code.includes('network') || code.includes('ECONNREFUSED')) {
        throw new Error('Auth service not reachable. If using emulators, start them first (Firestore:8080, Auth:9099). Otherwise, check your network.');
      }
      throw err;
    }
  })();

  const timeoutMs = 10000;
  const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Auth timeout. Start Firebase emulators or enable Anonymous Auth in Console.')), timeoutMs));
  
  // Store the promise globally so concurrent calls reuse it
  authPromise = Promise.race([signIn, timeout]);
  
  try {
    const user = await authPromise;
    return user;
  } catch (err) {
    // Clear the promise on error so next call can retry
    authPromise = null;
    throw err;
  }
}

// Optional helper for callers that want to await auth readiness explicitly
export async function ensureAuthReady() {
  return ensureAuth();
}

/**
 * CREATE - Create a new game room
 * @param {Object} params
 * @param {string} params.hostName - Display name of host
 * @param {string} params.award - What's at stake (e.g., "Pays for pizza")
 * @param {string} [params.roomId] - Optional room ID, will generate if not provided
 * @returns {Promise<Object>} Room data with roomId and hostId
 */
export async function createRoom({ hostName, award, roomId }) {
  try {
    await ensureAuth();
    const user = auth.currentUser;
    const finalRoomId = roomId || generateRoomId();
    const hostId = user.uid;

    // Guard: Never use root-level Game or NumberOfRounds
    // Always use a nested game object with key, name, Number_of_rounds
    // Example: game: { key: 'dice', name: 'Dice', Number_of_rounds: 3 }
    // If you see root-level Game or NumberOfRounds, throw an error
    // This ensures Firestore rules and all logic stay consistent

    // Default game config
    const defaultGame = { key: 'dice', name: 'Dice', Number_of_rounds: 1 };

    const roomRef = doc(db, 'RoomsTracker', finalRoomId);
    // Firestore rules require createdAt == request.time, so use serverTimestamp()
    // expiresAt must be > request.time, so set to 1 hour in future to avoid clock skew
    const expires = Timestamp.fromMillis(Date.now() + 30 * 60 * 1000); // 30 minutes
    const roomData = {
      roomId: finalRoomId,
      hostId: hostId,
      hostName: hostName, // store for quick display
      award: award,
      status: 'lobby',
      winner: null,
      createdAt: serverTimestamp(),
      expiresAt: expires // 1 hour
    };

    // Guard: error if anyone tries to add root-level Game or NumberOfRounds
    if ('Game' in roomData || 'NumberOfRounds' in roomData) {
      throw new Error('Do not use root-level Game or NumberOfRounds. Use a nested game object.');
    }

  console.log('[createRoom] roomData payload:', JSON.stringify(roomData));
  await setDoc(roomRef, roomData);
    
    // Create host player document in PlayersTracker
    const playerDocId = `${finalRoomId}_${hostId}`;
    const playerRef = doc(db, 'PlayersTracker', playerDocId);
    const playerData = {
      roomId: finalRoomId,
      playerId: hostId,
      name: hostName,
      joinedAt: serverTimestamp(),
      Round1: null,
      Round2: null,
      Round3: null,
      Round4: null,
      Round5: null,
      Round6: null,
      Round7: null,
      Round8: null,
      Round9: null
    };
    
    await setDoc(playerRef, playerData);
    
    return {
      roomId: finalRoomId,
      hostId: hostId,
      hostName: hostName,
      award: award,
      expiresAt: roomData.expiresAt.toDate()
    };
  } catch (error) {
    console.error('Create room error:', error);
    throw new Error(error.message || 'Failed to create room');
  }
}

/**
 * JOIN - Join an existing game room
 * @param {Object} params
 * @param {string} params.roomId - 6-digit room code
 * @param {string} params.name - Player's display name
 * @returns {Promise<Object>} Room and player data
 */
export async function joinRoom({ roomId, name }) {
  try {
    await ensureAuth();
    // Add a short delay to ensure UID is available
    await new Promise((resolve) => setTimeout(resolve, 250));
    const user = auth.currentUser;
    console.log('[joinRoom] After ensureAuth, user:', user);
    if (!user || !user.uid) {
      console.error('[joinRoom] No user or UID after ensureAuth:', user);
      throw new Error('Authentication failed. Please refresh and try again.');
    }
    const playerId = user.uid;
    console.log('[joinRoom] Using playerId:', playerId);

    // Check if room exists
    const roomRef = doc(db, 'RoomsTracker', roomId);
    const roomSnap = await getDoc(roomRef);
    console.log('[joinRoom] RoomSnap exists:', roomSnap.exists());

    if (!roomSnap.exists()) {
      console.error('[joinRoom] Room not found:', roomId);
      throw new Error('Room not found. Check the code and try again.');
    }

    const roomData = roomSnap.data();
    console.log('[joinRoom] RoomData:', roomData);

    // Check if room is still in lobby status
    if (roomData.status !== 'lobby') {
      console.error('[joinRoom] Room not in lobby status:', roomData.status);
      throw new Error('Room is no longer accepting players.');
    }

    // Create player document
    const playerDocId = `${roomId}_${playerId}`;
    const playerRef = doc(db, 'PlayersTracker', playerDocId);
    console.log('[joinRoom] playerDocId:', playerDocId);


    // Payload must match Firestore rules exactly
    const playerData = {
      roomId: roomId,
      playerId: playerId,
      name: name,
      joinedAt: serverTimestamp(),
      Round1: null,
      Round2: null,
      Round3: null,
      Round4: null,
      Round5: null,
      Round6: null,
      Round7: null,
      Round8: null,
      Round9: null
    };

    // Print keys and payload for diagnosis
    console.log('[joinRoom] playerData keys:', Object.keys(playerData));
    console.log('[joinRoom] playerData full:', JSON.stringify(playerData));
    // Print rule condition checks
    console.log('[joinRoom] Rule check: playerDocId == (roomId + _ + playerId):', playerDocId === (roomId + '_' + playerId));
    console.log('[joinRoom] Rule check: playerId == user.uid:', playerId === user.uid);
    console.log('[joinRoom] Rule check: roomId == roomId:', playerData.roomId === roomId);
    // Print rounds null check
    for (let i = 1; i <= 9; i++) {
      console.log(`[joinRoom] Rule check: Round${i} is null:`, playerData[`Round${i}`] === null);
    }

    // Debug log for diagnosis
    console.log('[joinRoom] Creating player doc:', playerDocId, playerData);

    // Use merge: false to enforce exact shape (create-first). If it fails, try rejoin path.
    try {
      await setDoc(playerRef, playerData, { merge: false });
      console.log('[joinRoom] setDoc success (created):', playerDocId);
    } catch (permErr) {
      console.warn('[joinRoom] setDoc permission error, attempting rejoin check:', permErr);
      try {
        const rejoinSnap = await getDoc(playerRef);
        console.log('[joinRoom] Rejoin read exists:', rejoinSnap.exists());
        if (rejoinSnap.exists()) {
          console.log('[joinRoom] Rejoining existing membership');
          return {
            roomId: roomId,
            playerId: playerId,
            award: roomData.award,
            hostId: roomData.hostId,
            hostName: roomData.hostName || null,
            expiresAt: roomData.expiresAt?.toDate() || null
          };
        }
      } catch (readErr) {
        console.warn('[joinRoom] Rejoin read failed (likely not a member yet):', readErr);
      }
      throw permErr;
    }

    return {
      roomId: roomId,
      playerId: playerId,
      award: roomData.award,
      hostId: roomData.hostId,
      hostName: roomData.hostName || null,
      expiresAt: roomData.expiresAt?.toDate() || null
    };
  } catch (error) {
    console.error('Join room error:', error);
    throw new Error(error.message || 'Failed to join room');
  }
}

/**
 * SYNC - Get current room state (one-time fetch)
 * @param {Object} params
 * @param {string} params.roomId - Room ID to sync
 * @returns {Promise<Object>} Room state and membership info
 */
export async function syncRoom({ roomId }) {
  try {
    await ensureAuth();
    const currentUid = auth.currentUser?.uid;
    
    const roomRef = doc(db, 'RoomsTracker', roomId);
    const roomSnap = await getDoc(roomRef);
    
    if (!roomSnap.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = roomSnap.data();
    
    // Check if current user is a member
    let isMember = false;
    let playerData = null;
    if (currentUid) {
      try {
        const playerDocId = `${roomId}_${currentUid}`;
        const playerRef = doc(db, 'PlayersTracker', playerDocId);
        const playerSnap = await getDoc(playerRef);
        isMember = playerSnap.exists();
        if (isMember) {
          playerData = playerSnap.data();
        }
      } catch (e) {
        console.warn('[syncRoom] Could not check membership:', e);
      }
    }
    
    return {
      room: roomData,
      award: roomData.award,
      hostId: roomData.hostId,
      hostName: roomData.hostName || null,
      expiresAt: roomData.expiresAt?.toDate() || null,
      isMember,
      playerId: isMember ? currentUid : null,
      playerName: playerData?.name || null
    };
  } catch (error) {
    console.error('Sync room error:', error);
    throw new Error(error.message || 'Failed to sync room');
  }
}

/**
 * LISTEN - Real-time listener for room updates
 * @param {string} roomId - Room ID to listen to
 * @param {Function} callback - Called with room data on updates
 * @returns {Function} Unsubscribe function
 */
export function listenToRoom(roomId, callback) {
  let unsub = null;
  let cancelled = false;

  console.log('[listenToRoom] Starting listener setup for room:', roomId);
  
  // Ensure we have credentials before opening the stream to avoid PERMISSION_DENIED spam
  ensureAuth()
    .then((user) => {
      if (cancelled) {
        console.log('[listenToRoom] Cancelled before auth completed');
        return;
      }
      console.log('[listenToRoom] Auth ready, user:', user?.uid, 'opening snapshot for room:', roomId);
      const roomRef = doc(db, 'RoomsTracker', roomId);
      unsub = onSnapshot(
        roomRef,
        (snapshot) => {
          if (snapshot.exists()) {
            console.log('[listenToRoom] Room snapshot received:', roomId);
            const data = snapshot.data();
            callback({
              room: data,
              award: data.award,
              hostId: data.hostId,
              expiresAt: data.expiresAt?.toDate() || null
            });
          } else {
            console.log('[listenToRoom] Room does not exist:', roomId);
            callback(null);
          }
        },
        (error) => {
          console.error('[listenToRoom] Room listener error:', roomId, error);
          callback(null, error);
        }
      );
    })
    .catch((err) => {
      console.error('[listenToRoom] Room listener auth error:', err);
      callback(null, err);
    });

  // Return a stable unsubscribe function immediately
  return () => {
    console.log('[listenToRoom] Unsubscribing from room:', roomId);
    cancelled = true;
    if (typeof unsub === 'function') {
      try { unsub(); } catch {}
    }
  };
}

/**
 * LISTEN TO PLAYERS - Real-time listener for all players in a room
 * @param {string} roomId - Room ID
 * @param {Function} callback - Called with array of players
 * @returns {Function} Unsubscribe function
 */
export function listenToPlayers(roomId, callback) {
  let unsub = null;
  let cancelled = false;

  console.log('[listenToPlayers] Starting listener setup for room:', roomId);

  // Ensure auth first; without it, rules will reject the query for non-public rooms
  ensureAuth()
    .then((user) => {
      if (cancelled) {
        console.log('[listenToPlayers] Cancelled before auth completed');
        return;
      }
      console.log('[listenToPlayers] Auth ready, user:', user?.uid, 'opening snapshot for room:', roomId);
      const playersQuery = query(
        collection(db, 'PlayersTracker'),
        where('roomId', '==', roomId)
      );
      unsub = onSnapshot(
        playersQuery,
        (snapshot) => {
          console.log('[listenToPlayers] Players snapshot received, size:', snapshot.size);
          console.log('[listenToPlayers] Snapshot metadata:', {
            fromCache: snapshot.metadata.fromCache,
            hasPendingWrites: snapshot.metadata.hasPendingWrites
          });
          const players = [];
          snapshot.forEach((d) => {
            console.log('[listenToPlayers] Document:', d.id, d.data());
            players.push({ id: d.id, ...d.data() });
          });
          console.log('[listenToPlayers] Total players collected:', players.length);
          console.log('[listenToPlayers] Players data:', players.map(p => ({ id: p.id, playerId: p.playerId, name: p.name })));
          callback(players);
        },
        (error) => {
          if (error.code === 'permission-denied') {
            console.warn('[listenToPlayers] Permission denied: user is not a member yet.');
            callback([], { code: 'permission-denied', message: 'Join to see players' });
            return;
          }
          console.error('[listenToPlayers] Players listener error:', roomId, error.code, error.message);
          callback([], error);
        }
      );
    })
    .catch((err) => {
      console.error('[listenToPlayers] Players listener auth error:', err);
      callback([], err);
    });

  return () => {
    console.log('[listenToPlayers] Unsubscribing from players for room:', roomId);
    cancelled = true;
    if (typeof unsub === 'function') {
      try { unsub(); } catch {}
    }
  };
}

/**
 * UPDATE ROOM - Update room settings (host only)
 * @param {Object} params
 * @param {string} params.roomId - Room ID
 * @param {Object} params.updates - Fields to update (status, award, expiresAt only)
 */
export async function updateRoom({ roomId, updates }) {
  try {
    await ensureAuth();
    
    const roomRef = doc(db, 'RoomsTracker', roomId);
    await updateDoc(roomRef, updates);
    
    return { success: true };
  } catch (error) {
    console.error('Update room error:', error);
    throw new Error(error.message || 'Failed to update room');
  }
}

/**
 * LOCK ROOM - Lock the room with selected game and config
 * @param {Object} params
 * @param {string} params.roomId - Room ID
 * @param {string} params.hostId - Host ID for verification
 * @param {Object} params.game - Game configuration {key, name, summary}
 * @param {Object} params.config - Game config {Number_of_rounds, etc}
 */
export async function lockRoom({ roomId, hostId, game, config }) {
  try {
    await ensureAuth();
    const currentUid = auth.currentUser?.uid;
    
    // Verify room exists and caller is host
    const roomRef = doc(db, 'RoomsTracker', roomId);
    const roomSnap = await getDoc(roomRef);
    
    if (!roomSnap.exists()) {
      throw new Error('Room not found');
    }
    
    const roomData = roomSnap.data();
    if (roomData.hostId !== currentUid && roomData.hostId !== hostId) {
      throw new Error('Only the host can lock the room');
    }
    
    if (roomData.status !== 'lobby') {
      throw new Error(`Cannot lock room in ${roomData.status} status`);
    }
    
    // Update room with game config and locked status
    await updateDoc(roomRef, {
      game: {
        key: game.key,
        name: game.name,
        summary: game.summary || '',
        ...config
      },
      status: 'locked',
      updatedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      status: 'locked',
      game: {
        key: game.key,
        name: game.name,
        ...config
      }
    };
  } catch (error) {
    console.error('Lock room error:', error);
    throw new Error(error.message || 'Failed to lock room');
  }
}

/**
 * UPDATE PLAYER ROUND - Update a player's round result
 * @param {Object} params
 * @param {string} params.roomId - Room ID
 * @param {string} params.playerId - Player ID
 * @param {number} params.roundNumber - Round number (1-9)
 * @param {*} params.value - Round result value
 */
export async function updatePlayerRound({ roomId, playerId, roundNumber, value }) {
  try {
    await ensureAuth();
    
    if (roundNumber < 1 || roundNumber > 9) {
      throw new Error('Invalid round number');
    }
    
    const playerDocId = `${roomId}_${playerId}`;
    const playerRef = doc(db, 'PlayersTracker', playerDocId);
    
    const updates = {
      [`Round${roundNumber}`]: value
    };
    
    await updateDoc(playerRef, updates);
    
    return { success: true };
  } catch (error) {
    console.error('Update player round error:', error);
    throw new Error(error.message || 'Failed to update round');
  }
}

/**
 * Check if current authed user has a PlayersTracker membership in the given room
 * @param {string} roomId
 * @returns {Promise<boolean>}
 */
export async function hasMembership(roomId) {
  await ensureAuth();
  const uid = auth.currentUser?.uid;
  if (!uid) return false;
  try {
    const playerDocId = `${roomId}_${uid}`;
    const ref = doc(db, 'PlayersTracker', playerDocId);
    const snap = await getDoc(ref);
    return snap.exists();
  } catch (e) {
    console.warn('[hasMembership] failed to read membership:', e);
    return false;
  }
}

/**
 * Legacy API compatibility wrapper
 * Maps old gameApi calls to new Firestore functions
 */
export async function api(method, payload) {
  switch (method) {
    case 'create':
      return createRoom(payload);
    
    case 'join':
      return joinRoom(payload);
    
    case 'sync':
      return syncRoom(payload);
    
    case 'updateRoom':
      return updateRoom(payload);
    
    case 'updatePlayerRound':
      return updatePlayerRound(payload);
    
    case 'action':
      return performAction(payload);
    
    default:
      throw new Error(`Unknown API method: ${method}`);
  }
}

/**
 * PERFORM ACTION - Handle game actions (lock, start, etc.)
 * @param {Object} params
 * @param {string} params.type - Action type ('lock', 'start', etc.)
 * @param {string} params.roomId - Room ID
 * @param {string} params.by - User ID performing action
 */
async function performAction({ type, roomId, by, ...params }) {
  switch (type) {
    case 'lock':
      return lockRoom({
        roomId,
        hostId: by,
        game: params.game || {},
        config: params.config || {}
      });
    case 'start':
      return startRoom({ roomId, hostId: by });
    case 'remove':
      throw new Error('Remove action not yet implemented');
    case 'leave':
      throw new Error('Leave action not yet implemented');
    default:
      throw new Error(`Unknown action type: ${type}`);
  }
}

/**
 * START ROOM - Set status to in_progress and generate numbers for each player for all rounds
 * @param {Object} params
 * @param {string} params.roomId - Room ID
 * @param {string} params.hostId - Host ID for verification
 */
  async function startRoom({ roomId, hostId }) {
    try {
      await ensureAuth();
      const currentUid = auth.currentUser?.uid;
      const roomRef = doc(db, 'RoomsTracker', roomId);
    const roomSnap = await getDoc(roomRef);
    if (!roomSnap.exists()) throw new Error('Room not found');
    const roomData = roomSnap.data();
    if (roomData.hostId !== currentUid && roomData.hostId !== hostId) {
      throw new Error('Only the host can start the room');
    }
      if (roomData.status !== 'locked') {
        throw new Error(`Cannot start room in ${roomData.status} status`);
      }
      // Determine game type and number of rounds
      const game = roomData.game || {};
      const gameKey = game.key || '';
      const numRounds = game.Number_of_rounds || 1;
      let playerCount = 0;
      try {
        const playersRef = collection(db, 'PlayersTracker');
        const playersSnapshot = await getDocs(query(playersRef, where('roomId', '==', roomId)));
        playerCount = playersSnapshot.size;
      } catch (statsErr) {
        console.warn('[startRoom] Failed to count players for stats logging:', statsErr);
      }
      // Only update room status; Cloud Function will generate numbers for all players
      await updateDoc(roomRef, { status: 'in_progress', updatedAt: serverTimestamp() });
      try {
        await recordWhoIsPayingUsage({
          roomId,
          gameKey,
          award: roomData.award || '',
          playerCount,
        });
      } catch (statsErr) {
        console.warn('[startRoom] Failed to record cross-product usage:', statsErr);
      }
      return { success: true, status: 'in_progress' };
    } catch (error) {
      console.error('Start room error:', error);
      throw new Error(error.message || 'Failed to start room');
    }
}
    
export default {
  api,
  createRoom,
  joinRoom,
  syncRoom,
  listenToRoom,
  listenToPlayers,
  hasMembership,
  updateRoom,
  updatePlayerRound,
  lockRoom
};
